import { Box, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { toast } from "react-toastify";

import { FormField, FormSelect } from "./FormControl";
import { pickerSlots } from "../../../types/constants";
import { BookingData } from "../../../../../types/bookings";
import PhotoEditor from "../../../../../components/PhotoEditor";

// ── Props ────────────────────────────────────────────────────────────────────

interface Props {
  bookingData: BookingData;
  setBookingData: React.Dispatch<React.SetStateAction<BookingData>>;
}

// ── Component ────────────────────────────────────────────────────────────────

export function DeceasedInformation({ bookingData, setBookingData }: Props) {
  // ── Deceased field updater ────────────────────────────────────────────────
  // Mirrors BookingServiceDetails.handleDeceasedChange:
  // always syncs index-0 in deceased_list AND the flat top-level field.
  const handleDeceasedChange = (field: string, value: string | File | null) => {
    setBookingData((prev) => {
      const list = prev.deceased_list ?? [];
      const updatedList =
        list.length > 0
          ? list.map((d, i) => (i === 0 ? { ...d, [field]: value } : d))
          : [{ [field]: value }];

      return { ...prev, [field]: value, deceased_list: updatedList };
    });
  };

  // ── Photo save handler ────────────────────────────────────────────────────
  // Mirrors BookingServiceDetails.handleSaveEditedImage
  const handleSaveEditedImage = async (imageData: string) => {
    try {
      const res = await fetch(imageData);
      const blob = await res.blob();
      const file = new File([blob], "deceased_image_0.jpg", {
        type: blob.type,
      });

      setBookingData((prev) => {
        const list = prev.deceased_list ?? [];
        const updatedList =
          list.length > 0
            ? list.map((d, i) =>
                i === 0
                  ? { ...d, photo_url: imageData, deceasedImageFile: file }
                  : d,
              )
            : [{ photo_url: imageData, deceasedImageFile: file }];

        return {
          ...prev,
          photo_url: imageData,
          deceasedImageFile: file,
          deceased_list: updatedList,
        };
      });
    } catch (error) {
      console.error("Failed to process image:", error);
      toast.error("Failed to process image. Please try again.", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
    }
  };

  // ── Date helpers ──────────────────────────────────────────────────────────
  const toDate = (val?: string | null): Dayjs | null =>
    val ? dayjs(val) : null;
  const fromDate = (d: Dayjs | null): string =>
    d ? d.format("YYYY-MM-DD") : "";

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} mb={3}>
        Deceased Information
      </Typography>

      {/* ── Photo upload (PhotoEditor replaces the old file-drop box) ──────── */}
      <Box mb={3}>
        <PhotoEditor
          initialImage={
            bookingData.deceased_list?.[0]?.person_image ??
            bookingData.photo_url ??
            null
          }
          onSave={handleSaveEditedImage}
          title="Upload Deceased Image"
        />
      </Box>

      {/* ── Title / Name / Surname ────────────────────────────────────────── */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "auto 1fr 1fr" },
          gap: 2,
        }}
      >
        <Box sx={{ mt: "-4px" }}>
          <FormSelect
            label="Title"
            value={bookingData.deceased_salutation ?? ""}
            onChange={(v) => handleDeceasedChange("deceased_salutation", v)}
            options={["Mr", "Mrs", "Ms", "Miss", "Dr", "Prof", "Rev", "Other"]}
            minWidth={85}
          />
        </Box>
        <FormField
          label="Deceased Name"
          value={bookingData.deceased_name ?? ""}
          onChange={(v) => handleDeceasedChange("deceased_name", v)}
        />
        <FormField
          label="Deceased Surname"
          value={bookingData.deceased_surname ?? ""}
          onChange={(v) => handleDeceasedChange("deceased_surname", v)}
        />
      </Box>

      {/* ── Gender ───────────────────────────────────────────────────────── */}
      <Box sx={{ mt: 2, width: { xs: "100%", sm: 140 } }}>
        <FormSelect
          label="Gender"
          value={bookingData.gender ?? ""}
          onChange={(v) => handleDeceasedChange("gender", v)}
          options={["Male", "Female", "Other"]}
          minWidth={60}
        />
      </Box>

      {/* ── DOB / DOD ────────────────────────────────────────────────────── */}
      {/* ── DOB / DOD ────────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 2,
          mt: 2,
          maxWidth: { xs: "100%", sm: "calc(100% - 85px - 16px)" },
        }}
      >
        <DatePicker
          label="Sunrise (Date of Birth)"
          value={toDate(bookingData.date_of_birth)}
          onChange={(d) =>
            handleDeceasedChange("date_of_birth", fromDate(d as Dayjs | null))
          }
          slotProps={pickerSlots}
          sx={{ flex: 1 }}
        />
        <DatePicker
          label="Sunset (Date of Death)"
          value={toDate(bookingData.date_of_death)}
          onChange={(d) =>
            handleDeceasedChange("date_of_death", fromDate(d as Dayjs | null))
          }
          slotProps={pickerSlots}
          sx={{ flex: 1 }}
        />
      </Box>
    </Box>
  );
}
